import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Clock, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createAppointment } from "@/lib/api";
import { cn } from "@/lib/utils";
import { APPOINTMENT } from "@/lib/testIds";

const timeSlots = ["09:00", "10:00", "11:00", "12:00", "14:00", "15:00", "16:00"];
const topics = [
  { v: "leasing", l: "Leasing an accommodation" },
  { v: "sales", l: "Buying a property" },
  { v: "landlord", l: "Landlord / Portfolio management" },
  { v: "services", l: "Technical services (QS, Architect, Structural)" },
  { v: "other", l: "Other" },
];

export default function BookAppointment() {
  const [date, setDate] = useState();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", time_slot: "", topic: "leasing", notes: "",
  });

  const mutation = useMutation({
    mutationFn: createAppointment,
    onSuccess: () => {
      toast.success("Appointment booked!", { description: "Our team will confirm your slot shortly." });
      setForm({ name: "", email: "", phone: "", company: "", time_slot: "", topic: "leasing", notes: "" });
      setDate(undefined);
    },
    onError: () => toast.error("Something went wrong — please try again."),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!date) return toast.error("Please pick a date.");
    if (!form.time_slot) return toast.error("Please select a time slot.");
    mutation.mutate({ ...form, date: format(date, "yyyy-MM-dd") });
  };

  return (
    <div>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 md:pt-32 pb-12">
        <p className="overline text-secondary">Book Appointment</p>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-serif max-w-4xl leading-tight">
          Meet with us for a detailed presentation of our services.
        </h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
          Pick a date and time that works for you. Our team will confirm your
          slot and share a personalised agenda before the meeting.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid lg:grid-cols-12 gap-10">
        <form onSubmit={handleSubmit} className="lg:col-span-8 bg-background border border-border p-8 md:p-10 space-y-6" data-testid="appointment-form">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="a-name">Full name</Label>
              <Input id="a-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-sm h-11" data-testid={APPOINTMENT.name} />
            </div>
            <div>
              <Label htmlFor="a-email">Email</Label>
              <Input id="a-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-sm h-11" data-testid={APPOINTMENT.email} />
            </div>
            <div>
              <Label htmlFor="a-phone">Phone</Label>
              <Input id="a-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-sm h-11" data-testid={APPOINTMENT.phone} />
            </div>
            <div>
              <Label htmlFor="a-company">Company (optional)</Label>
              <Input id="a-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1.5 rounded-sm h-11" data-testid={APPOINTMENT.company} />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label>Preferred date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("mt-1.5 w-full justify-start text-left h-11 rounded-sm font-normal", !date && "text-muted-foreground")} data-testid={APPOINTMENT.date}>
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {date ? format(date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 0}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label>Preferred time</Label>
              <Select value={form.time_slot} onValueChange={(v) => setForm({ ...form, time_slot: v })}>
                <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid={APPOINTMENT.timeSlot}>
                  <SelectValue placeholder="Pick a time slot" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>What&apos;s this about?</Label>
            <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
              <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid={APPOINTMENT.topic}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {topics.map((t) => (
                  <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="a-notes">Notes (optional)</Label>
            <Textarea id="a-notes" rows={5} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 rounded-sm" data-testid={APPOINTMENT.notes} placeholder="Tell us a bit about the building, portfolio or property you'd like to discuss." />
          </div>

          <Button type="submit" disabled={mutation.isPending} className="rounded-sm h-12 px-8 bg-primary text-primary-foreground" data-testid={APPOINTMENT.submit}>
            {mutation.isPending ? "Booking…" : "Book Appointment"}
          </Button>
        </form>

        {/* Aside */}
        <aside className="lg:col-span-4 space-y-6">
          <div className="bg-primary text-primary-foreground p-8">
            <User className="w-8 h-8 text-secondary" strokeWidth={1.5} />
            <p className="mt-6 overline text-secondary">Get in touch</p>
            <h3 className="mt-2 font-serif text-2xl">Campusco Leasing Team</h3>
            <p className="mt-1 text-sm text-primary-foreground/75">Landlord partnerships, leasing enquiries and building tours.</p>
            <div className="mt-6 space-y-2 text-sm">
              <p><a className="hover:text-secondary" href="mailto:info@thecampusco.co.za">info@thecampusco.co.za</a></p>
              <p><a className="hover:text-secondary" href="tel:+27608362116">060 836 2116</a></p>
            </div>
          </div>

          <div className="bg-background border border-border p-6">
            <p className="overline text-secondary">What to expect</p>
            <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
              <li className="flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5 text-secondary" /> 30-minute conversation</li>
              <li className="flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5 text-secondary" /> A tailored services overview</li>
              <li className="flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5 text-secondary" /> Straight answers on fees and terms</li>
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}
